import { UserRole } from '@/apolloClient.types';
import { useUser } from '@/contexts/UserSession';

const useProgramManagementAbilities = () => {
    const user = useUser(true);
    const role = user.__typename === 'TenantUser' ? user.role : null;
    const company = user.__typename === 'TenantUser' ? user.company : null;
    const isSuperAdmin = user?.isSuperAdmin || false;

    const canViewPrograms = () => {
        return !!company?.id;
    };

    const canCreateProgram = () => {
        return isSuperAdmin || (!!company && role === UserRole.CompanyAdmin);
    };

    const canEditProgram = () => {
        return isSuperAdmin || (!!company && role === UserRole.CompanyAdmin);
    };

    const canDeleteProgram = () => {
        return isSuperAdmin || (!!company && role === UserRole.CompanyAdmin);
    };

    const canViewCourseTemplates = () => {
        return !!company && [UserRole.CompanyAdmin, UserRole.CourseAdmin, UserRole.Trainer].includes(role!);
    };

    const canEditCourseTemplates = () => {
        return isSuperAdmin;
    };

    const canViewCourseInstances = () => {
        return !!company;
    };

    const canCreateCourseInstance = () => {
        // CompanyAdmin can create course instances
        return !!company && role === UserRole.CompanyAdmin;
    };

    const canEditCourseInstance = () => {
        // CompanyAdmin can edit course instances
        return !!company && role === UserRole.CompanyAdmin;
    };

    const canDeleteCourseInstance = () => {
        // CompanyAdmin can delete course instances
        return !!company && role === UserRole.CompanyAdmin;
    };

    const checkCanManageCourseInstancesForCompany = (companyId: string) => {
        return !!company?.id && company.id === companyId && role === UserRole.CompanyAdmin;
    };

    const canViewAssessmentRuns = () => {
        return !!company?.id;
    };

    const canCreateAssessmentRuns = () => {
        return isSuperAdmin || (!!company && role === UserRole.CompanyAdmin);
    };

    const canEditAssessmentRuns = () => {
        return isSuperAdmin || (!!company && role === UserRole.CompanyAdmin);
    };

    const canDeleteAssessmentRuns = () => {
        return isSuperAdmin || (!!company && role === UserRole.CompanyAdmin);
    };

    const displayTraineeProgramMenuItem = () => {
        return [UserRole.Trainee].includes(role!) && (canViewPrograms() || canViewCourseInstances());
    };

    const displayProgramManagementMenuItem = () => {
        return (
            [UserRole.CompanyAdmin, UserRole.CourseAdmin, UserRole.Trainer].includes(role!) &&
            (canViewPrograms() || canViewCourseInstances() || canViewAssessmentRuns() || canViewCourseTemplates())
        );
    };

    return {
        canViewPrograms,
        canCreateProgram,
        canEditProgram,
        canDeleteProgram,
        canViewAssessmentRuns,
        canCreateAssessmentRuns,
        canEditAssessmentRuns,
        canDeleteAssessmentRuns,
        canViewCourseInstances,
        canViewCourseTemplates,
        canEditCourseTemplates,
        canCreateCourseInstance,
        canEditCourseInstance,
        canDeleteCourseInstance,
        checkCanManageCourseInstancesForCompany,
        displayProgramManagementMenuItem,
        displayTraineeProgramMenuItem,
    };
};

export type ProgramManagementAbilities = ReturnType<typeof useProgramManagementAbilities>;
export default useProgramManagementAbilities;
